import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Menu,
  RefreshCw,
  Search,
  User,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import ProjectsSidebar from '../components/ProjectsSidebar';
import PlannerSidebar from '../components/PlannerSidebar';
import TimerWidget from '../components/TimeBilling/TimerWidget';
import SupportChatWidget from '../components/support/SupportChatWidget';
import { useAuth } from '../auth/useAuth';
import usePermissions, { getPermissionKeyForPath } from '../auth/usePermissions';
import notify from '../utils/notify';
import { fetchAdminSupportConversations } from '../utils/supportChat.js';
import { describeQuickBooksDisconnect } from '../utils/quickbooksDisconnect.js';
import '../styles/DashboardLayout.css';
import API_BASE from '../utils/api.js';

export default function DashboardLayout() {
  const { auth, logout } = useAuth();
  const isPremium = auth?.plan === 'premium';
  const { permissions, permissionsLoaded } = usePermissions();
  const location = useLocation();
  const [activeSidebar, setActiveSidebar] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResolveBusy, setSyncResolveBusy] = useState(false);
  const [syncSummary, setSyncSummary] = useState({
    issues: [],
    environment: null,
    connected: false,
    disconnect: null,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const mainContentRef = useRef(null);
  const navigate = useNavigate();
  const apiUrl = API_BASE;

  useEffect(() => {
    if (!permissionsLoaded) return;
    const key = getPermissionKeyForPath(location.pathname);
    if (key && permissions[key] === false) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, permissions, permissionsLoaded, navigate]);

  const toggleSidebar = (sidebar) =>
    setActiveSidebar((prev) => (prev === sidebar ? null : sidebar));

  const closeAllSidebars = () => {
    setActiveSidebar(null);
    setSidebarOpen(false);
  };

  useEffect(() => {
    let cancelled = false;
    async function checkAdminUnread() {
      try {
        const data = await fetchAdminSupportConversations();
        if (cancelled) return;
        const total = (data?.conversations || []).reduce(
          (sum, c) => sum + (Number(c.unreadForAdmin) || 0),
          0
        );
        setAdminUnreadCount(total);
      } catch {
        // not a support admin or fetch failed — no badge
      }
    }
    checkAdminUnread();
    const interval = setInterval(checkAdminUnread, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth <= 1024;
    document.body.style.overflow = isMobile && (sidebarOpen || activeSidebar) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen, activeSidebar]);

  useEffect(() => {
    const resize = () => window.innerWidth > 1024 && setSidebarOpen(false);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const el = mainContentRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 8);
    onScroll();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const click = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSyncOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener('pointerdown', click);
    return () => document.removeEventListener('pointerdown', click);
  }, []);

  /* -----------------------------------------------------------
     SIGN OUT (SYNCED WITH AUTH CONTEXT)
  ----------------------------------------------------------- */
  const handleSignOut = async () => {
    try {
      await fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    await logout(); // ✅ SINGLE SOURCE OF TRUTH
    navigate('/', { replace: true });
  };

  const runSearch = async (value) => {
    const userId = localStorage.getItem('userId');
    if (!userId || value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await fetch(
        `${apiUrl}/api/search?q=${encodeURIComponent(value)}&userId=${encodeURIComponent(userId)}`
      );
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const handleSearch = (value) => {
    const nextValue = value ?? '';
    setSearchQuery(nextValue);
    if (nextValue.trim().length < 2) {
      setSearchResults([]);
      clearTimeout(searchDebounceRef.current);
      return;
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => runSearch(nextValue), 300);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    clearTimeout(searchDebounceRef.current);
    runSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const getSearchResultHref = (item) => {
    if (item.type === 'Product') return '/products';
    if (item.type === 'Contact' || item.type === 'Vendor') return '/planner/contacts';
    if (item.type === 'Task') return '/planner/todo';
    if (item.type === 'Calendar Event') return '/planner/calendar';
    if (item.path) return item.path;
    return '#';
  };

  const handleSearchResultClick = (e, item) => {
    if (!item) return;
    e.preventDefault();

    if (item.type === 'Product' && item.entityId) {
      navigate('/products', { state: { editItemId: item.entityId } });
      clearSearch();
      return;
    }

    if ((item.type === 'Contact' || item.type === 'Vendor') && item.entityId) {
      navigate('/planner/contacts', { state: { editContactId: item.entityId } });
      clearSearch();
      return;
    }

    if (item.type === 'Task') {
      navigate('/planner/todo');
      clearSearch();
      return;
    }

    if (item.type === 'Calendar Event') {
      navigate('/planner/calendar');
      clearSearch();
      return;
    }

    if (item.path) {
      navigate(item.path);
      clearSearch();
    }
  };

  const loadSyncStatus = async () => {
    const userEmail = String(localStorage.getItem('userEmail') || '').trim().toLowerCase();
    const idToken = localStorage.getItem('idToken');
    setSyncLoading(true);
    try {
      const statusRes = await fetch(`${apiUrl}/api/quickbooks/status`, {
        credentials: 'include',
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
      });
      const statusData = await statusRes.json().catch(() => ({}));
      if (!statusRes.ok) {
        throw new Error(statusData?.error || 'Unable to load sync status.');
      }

      const issues = [];
      const currentEnvironment = String(statusData?.environment || '').trim();
      const availableEnvironments = Array.isArray(statusData?.availableEnvironments)
        ? statusData.availableEnvironments
        : [];
      const qbEmail = String(statusData?.qbUser?.email || '').trim().toLowerCase();
      const disconnect = statusData?.disconnect || null;

      // QuickBooks is optional, so an account that never connected it has nothing
      // to fix, and neither does one where someone chose to disconnect. Only a
      // connection QuickBooks dropped on its own needs attention.
      if (!statusData?.connected && disconnect && disconnect.reason !== 'disconnected_in_app') {
        issues.push({
          key: 'not-connected',
          title: 'QuickBooks was disconnected',
          detail:
            describeQuickBooksDisconnect(disconnect) ||
            "QuickBooks stopped accepting InteriorFolio's connection. Reconnect to resume syncing.",
          resolvable: true,
          resolveAction: 'reconnect',
        });
      }

      if (statusData?.connected && qbEmail && userEmail && qbEmail !== userEmail) {
        issues.push({
          key: 'account-mismatch',
          title: 'Connected to a different QuickBooks account',
          detail: `Signed in as ${statusData.qbUser.email}. InteriorFolio user is ${userEmail}.`,
          resolvable: true,
          resolveAction: 'reconnect',
        });
      }

      if (statusData?.connected && statusData?.companySwitch?.pending) {
        issues.push({
          key: 'company-switch',
          title: 'Connected to a different QuickBooks company',
          detail: 'QuickBooks was reconnected to a different company. Syncing is paused, and existing proposals, invoices, and purchase orders won’t sync until you resolve this — resolving clears their old links so each can be synced fresh to the new company.',
          resolvable: true,
          resolveAction: 'resolve-company-switch',
        });
      }

      if (availableEnvironments.length > 1) {
        issues.push({
          key: 'multi-env',
          title: 'Multiple QuickBooks environments are linked',
          detail: `Detected ${availableEnvironments.join(', ')}. Keep one environment to avoid split sync history.`,
          resolvable: true,
          resolveAction: 'resolve-multi-env',
        });
      }

      if (statusData?.connected) {
        const snapshotRes = await fetch(`${apiUrl}/api/quickbooks/default-accounts/snapshot`, {
          credentials: 'include',
          headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
        });
        const snapshotData = await snapshotRes.json().catch(() => ({}));
        if (snapshotRes.ok && Array.isArray(snapshotData?.missing) && snapshotData.missing.length) {
          issues.push({
            key: 'missing-default-accounts',
            title: 'QuickBooks default accounts are out of sync',
            detail: `Not found in QuickBooks: ${snapshotData.missing.join(', ')}. InteriorFolio won't create accounts in your QuickBooks file — add or rename an account to match, then resolve again.`,
            resolvable: true,
            resolveAction: 'bootstrap-default-accounts',
          });
        }
      }

      const nextSummary = {
        connected: Boolean(statusData?.connected),
        environment: currentEnvironment || null,
        disconnect,
        issues,
      };
      setSyncSummary(nextSummary);
      return nextSummary;
    } catch (err) {
      const message = err?.message || 'Unable to load sync status.';
      const nextSummary = {
        connected: false,
        environment: null,
        issues: [
          {
            key: 'status-fetch-failed',
            title: 'Sync status could not be verified',
            detail: message,
            resolvable: true,
            resolveAction: 'connect',
          },
        ],
      };
      setSyncSummary(nextSummary);
      return nextSummary;
    } finally {
      setSyncLoading(false);
    }
  };

  const resolveSyncIssues = async () => {
    if (syncResolveBusy) return;
    setSyncResolveBusy(true);
    try {
      const idToken = localStorage.getItem('idToken');
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      };
      const hasMultiEnvIssue = syncSummary.issues.some(
        (issue) => issue.resolveAction === 'resolve-multi-env'
      );
      const hasCompanySwitchIssue = syncSummary.issues.some(
        (issue) => issue.resolveAction === 'resolve-company-switch'
      );
      const hasAccountBootstrapIssue = syncSummary.issues.some(
        (issue) => issue.resolveAction === 'bootstrap-default-accounts'
      );
      const hasConnectIssue = syncSummary.issues.some(
        (issue) => issue.resolveAction === 'connect' || issue.resolveAction === 'reconnect'
      );

      if (hasCompanySwitchIssue) {
        const res = await fetch(`${apiUrl}/api/quickbooks/resolve-sync-issues`, {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders,
          body: JSON.stringify({ issueType: 'company-switch' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to reset stale QuickBooks links.');
      }

      if (hasMultiEnvIssue) {
        const res = await fetch(`${apiUrl}/api/quickbooks/resolve-sync-issues`, {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders,
          body: JSON.stringify({ issueType: 'multi-env' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to resolve multi-environment sync issue.');
      }

      if (hasAccountBootstrapIssue) {
        const bootstrapRes = await fetch(`${apiUrl}/api/quickbooks/default-accounts/bootstrap`, {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders,
          body: JSON.stringify({}),
        });
        const bootstrapData = await bootstrapRes.json().catch(() => ({}));
        if (!bootstrapRes.ok) {
          throw new Error(bootstrapData?.error || 'Failed to bootstrap QuickBooks default accounts.');
        }
      }

      const refreshed = await loadSyncStatus();
      if ((refreshed?.issues || []).length === 0) {
        notify.success('Sync issues resolved.');
      } else if (hasAccountBootstrapIssue) {
        const stillMissing = refreshed.issues.find((issue) => issue.key === 'missing-default-accounts');
        notify.error(
          stillMissing?.detail ||
            'Some QuickBooks default accounts still need attention — see Sync Status for details.'
        );
      }

      if (hasConnectIssue) {
        navigate('/settings/quickbooks');
      }
    } catch (err) {
      console.error('Resolve sync issue error:', err);
      notify.error(err?.message || 'Failed to resolve sync issues.');
    } finally {
      setSyncResolveBusy(false);
    }
  };

  const handleIssueResolveAction = async (action) => {
    if (syncResolveBusy) return;
    if (!action) return;

    if (action === 'connect' || action === 'reconnect') {
      setSyncOpen(false);
      navigate('/settings/quickbooks');
      notify.info('Open QuickBooks settings and click Connect/Reconnect to finish.');
      return;
    }

    if (action === 'resolve-multi-env') {
      setSyncResolveBusy(true);
      try {
        const idToken = localStorage.getItem('idToken');
        const res = await fetch(`${apiUrl}/api/quickbooks/resolve-sync-issues`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({ issueType: 'multi-env' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to remove extra QuickBooks environment links.');
        await loadSyncStatus();
        notify.success('Removed extra QuickBooks environment links.');
      } catch (err) {
        console.error('Issue-specific resolve error:', err);
        notify.error(err?.message || 'Failed to resolve sync issue.');
      } finally {
        setSyncResolveBusy(false);
      }
      return;
    }

    if (action === 'resolve-company-switch') {
      setSyncResolveBusy(true);
      try {
        const idToken = localStorage.getItem('idToken');
        const res = await fetch(`${apiUrl}/api/quickbooks/resolve-sync-issues`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({ issueType: 'company-switch' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to reset stale QuickBooks links.');
        await loadSyncStatus();
        const unlinked = data?.unlinked || {};
        const unlinkedCount = Object.values(unlinked).reduce((sum, n) => sum + (Number(n) || 0), 0);
        notify.success(
          unlinkedCount > 0
            ? `Cleared ${unlinkedCount} old QuickBooks link(s). Use each record's "Sync to QuickBooks" action to push it to the new company.`
            : 'QuickBooks sync is unblocked.'
        );
      } catch (err) {
        console.error('Issue-specific resolve error:', err);
        notify.error(err?.message || 'Failed to resolve sync issue.');
      } finally {
        setSyncResolveBusy(false);
      }
      return;
    }

    if (action === 'bootstrap-default-accounts') {
      setSyncResolveBusy(true);
      try {
        const idToken = localStorage.getItem('idToken');
        const res = await fetch(`${apiUrl}/api/quickbooks/default-accounts/bootstrap`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to bootstrap default accounts.');
        await loadSyncStatus();
        const stillMissing = Array.isArray(data?.missing) ? data.missing : [];
        if (stillMissing.length === 0) {
          notify.success('QuickBooks default accounts synced.');
        } else {
          notify.error(
            `Not found in QuickBooks: ${stillMissing.join(', ')}. InteriorFolio won't create accounts in your QuickBooks file — add or rename an account to match, then resolve again.`
          );
        }
      } catch (err) {
        console.error('Issue-specific resolve error:', err);
        notify.error(err?.message || 'Failed to resolve sync issue.');
      } finally {
        setSyncResolveBusy(false);
      }
    }
  };

  const syncIssueCount = syncSummary.issues.length;
  const syncHasIssues = syncIssueCount > 0;
  const syncDisconnectNote = describeQuickBooksDisconnect(syncSummary.disconnect);

  return (
    <div className="dashboard-layout-wrapper">
      <header className={`top-header ${scrolled ? 'scrolled' : ''}`}>
        <form className="search-bar" ref={searchRef} onSubmit={handleSearchSubmit}>
          <button type="submit" className="search-submit" aria-label="Search">
            <Search size={16} />
          </button>
          <input
            type="text"
            placeholder="Search for anything..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />

          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((item, i) => (
                <a
                  key={i}
                  href={getSearchResultHref(item)}
                  className="search-result-item"
                  onClick={(e) => handleSearchResultClick(e, item)}
                >
                  <div className="search-result-main">
                    <strong>{item.label}</strong>
                    <span className="search-result-type">{item.type}</span>
                  </div>
                  {item.snippet && item.snippet !== item.label && (
                    <div className="search-result-snippet">{item.snippet}</div>
                  )}
                </a>
              ))}
            </div>
          )}
        </form>

        <div className="header-right" ref={dropdownRef}>
          {isPremium && permissions.timeBilling !== false && <TimerWidget />}

          {isPremium && (
            <button
              className={`sync-status-btn ${syncHasIssues ? 'has-issues' : 'ok'}`}
              onClick={async () => {
                const next = !syncOpen;
                setSyncOpen(next);
                setDropdownOpen(false);
                if (next) {
                  await loadSyncStatus();
                }
              }}
              aria-label="Sync status"
            >
              <RefreshCw size={19} />
              {syncHasIssues && <span className="sync-badge">{syncIssueCount}</span>}
            </button>
          )}

          {syncOpen && isPremium && (
            <div className="sync-status-dropdown">
              <div className="sync-status-header">Sync Status</div>
              {syncLoading ? (
                <div className="sync-status-loading">
                  <Loader2 size={15} className="spin" />
                  Checking sync...
                </div>
              ) : syncHasIssues ? (
                <>
                  <div className="sync-status-count">{syncIssueCount} item(s) need attention</div>
                  <div className="sync-status-issues">
                    {syncSummary.issues.map((issue) => (
                      <button
                        type="button"
                        className={`sync-status-issue ${issue.resolvable ? 'is-clickable' : ''}`}
                        key={issue.key}
                        onClick={() => handleIssueResolveAction(issue.resolveAction)}
                        disabled={!issue.resolvable || syncResolveBusy}
                      >
                        <div className="sync-status-issue-title">
                          <AlertTriangle size={14} />
                          <span>{issue.title}</span>
                        </div>
                        <div className="sync-status-issue-detail">{issue.detail}</div>
                      </button>
                    ))}
                  </div>
                  <button
                    className="sync-resolve-btn"
                    onClick={resolveSyncIssues}
                    disabled={syncResolveBusy}
                  >
                    {syncResolveBusy ? 'Resolving...' : 'Resolve'}
                  </button>
                </>
              ) : syncSummary.connected ? (
                <div className="sync-status-ok">
                  <CheckCircle2 size={15} />
                  <span>
                    All syncs are aligned
                    {syncSummary.environment ? ` (${syncSummary.environment})` : ''}.
                  </span>
                </div>
              ) : (
                <div className="sync-status-optional">
                  <div className="sync-status-optional-title">
                    <Info size={14} />
                    <span>QuickBooks isn't connected</span>
                  </div>
                  {syncDisconnectNote && (
                    <div className="sync-status-issue-detail">{syncDisconnectNote}</div>
                  )}
                  <div className="sync-status-issue-detail">
                    Connecting it is optional: it's only used to sync proposals, invoices and
                    purchase orders to QuickBooks. Everything else, including the Clipper, works
                    without it.
                  </div>
                  {permissions.settingsQuickBooks !== false && (
                    <button
                      className="sync-resolve-btn"
                      onClick={() => handleIssueResolveAction('connect')}
                    >
                      Connect QuickBooks
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="profile-btn-wrapper">
            <button
              className="profile-btn"
              onClick={() => {
                setSyncOpen(false);
                setDropdownOpen((v) => !v);
              }}
            >
              <User size={18} />
            </button>
            {adminUnreadCount > 0 && <span className="profile-unread-dot" />}
          </div>

          {dropdownOpen && (
            <div className="profile-dropdown">
              <Link to="/support" onClick={() => setDropdownOpen(false)}>Support</Link>
              <Link to="/settings" onClick={() => setDropdownOpen(false)}>Settings</Link>
              <button onClick={handleSignOut} className="signout-btn">
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      <div className={`dashboard-layout ${activeSidebar ? `${activeSidebar}-open` : ''}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen((p) => !p)}>
          <Menu />
        </button>

        <div
          className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={closeAllSidebars}
        />

        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <Sidebar
            onToggleProjects={() => toggleSidebar('projects')}
            onTogglePlanner={() => toggleSidebar('planner')}
            activeSidebar={activeSidebar}
            permissions={permissions}
          />
        </aside>

        <ProjectsSidebar isOpen={activeSidebar === 'projects'} onClose={closeAllSidebars} />
        <PlannerSidebar isOpen={activeSidebar === 'planner'} onClose={closeAllSidebars} />

        <main className="main-content" ref={mainContentRef}>
          <Outlet /> {/* ✅ REQUIRED AND PRESENT */}
        </main>
      </div>

      <SupportChatWidget />
    </div>
  );
}
